import { useEffect } from 'react';
import { Modal, Stack, Select, MultiSelect, Textarea, Button, Group } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { toDateValue, toISODateString } from '../../../utils/dateHelpers';
import { notifications } from '@mantine/notifications';
import { coachesApi } from '../../../services/api/coaches';
import type { Coach, UpdateCoachRequest } from '../../../types';
import {
  RACE_ETHNICITY_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  PRONOUNS_OPTIONS,
} from '../../../config/coachConfig';

interface EditIdentityModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditIdentityModal({ opened, onClose, coach, onSaved }: EditIdentityModalProps) {
  const form = useForm<{
    birthday: Date | null;
    pronouns: string | null;
    gender_identity: string | null;
    race_ethnicity: string[];
    additional_identity_notes: string;
  }>({
    initialValues: {
      birthday: null,
      pronouns: null,
      gender_identity: null,
      race_ethnicity: [],
      additional_identity_notes: '',
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        birthday: coach.birthday ? new Date(coach.birthday) : null,
        pronouns: coach.pronouns || null,
        gender_identity: coach.gender_identity || null,
        race_ethnicity: coach.race_ethnicity || [],
        additional_identity_notes: coach.additional_identity_notes || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when modal opens with new coach data
  }, [opened, coach]);

  const handleSubmit = async (values: typeof form.values) => {
    try {
      const updateData: UpdateCoachRequest = {
        birthday: toISODateString(values.birthday) ?? null,
        pronouns: values.pronouns,
        gender_identity: values.gender_identity,
        race_ethnicity: values.race_ethnicity,
        additional_identity_notes: values.additional_identity_notes,
      };

      const updatedCoach = await coachesApi.update(coach.id, updateData);
      notifications.show({
        title: 'Success',
        message: 'Identity information updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update identity information',
        color: 'red',
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Identity & Demographics" size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <DateInput
            label="Birthday"
            placeholder="Select date"
            valueFormat="MM/DD/YYYY"
            clearable
            maxDate={new Date()}
            value={form.values.birthday}
            onChange={(value) => form.setFieldValue('birthday', toDateValue(value))}
            error={form.errors.birthday}
          />
          <Select
            label="Pronouns"
            placeholder="Select pronouns"
            clearable
            data={PRONOUNS_OPTIONS.map((p) => ({ value: p, label: p }))}
            {...form.getInputProps('pronouns')}
          />
          <Select
            label="Gender Identity"
            placeholder="Select gender identity"
            clearable
            data={GENDER_IDENTITY_OPTIONS.map((g) => ({ value: g, label: g }))}
            {...form.getInputProps('gender_identity')}
          />
          <MultiSelect
            label="Race/Ethnicity"
            placeholder="Select all that apply"
            data={RACE_ETHNICITY_OPTIONS.map((r) => ({ value: r, label: r }))}
            {...form.getInputProps('race_ethnicity')}
          />
          <Textarea
            label="Additional Notes"
            placeholder="Any additional identity information you'd like to share"
            minRows={2}
            {...form.getInputProps('additional_identity_notes')}
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
